"use client";

import type { CalendarDate, DateValue } from "@internationalized/date";
import {
  createCalendar,
  getLocalTimeZone,
  getWeeksInMonth,
  isToday,
  startOfWeek,
} from "@internationalized/date";
import type { AriaButtonProps } from "@react-aria/button";
import { useButton } from "@react-aria/button";
import type { AriaCalendarProps } from "@react-aria/calendar";
import { useCalendar, useCalendarCell, useCalendarGrid } from "@react-aria/calendar";
import { I18nProvider, useDateFormatter, useLocale } from "@react-aria/i18n";
import type { CalendarState } from "@react-stately/calendar";
import { useCalendarState } from "@react-stately/calendar";
import * as React from "react";

import { cn } from "@/lib/cn";

import { Icon } from "@/components/ui/Icon";

import "@/components/calendar/CalendarCell.css";

// Re-export types for external use
export type { DateValue } from "@internationalized/date";

// Постоянная высота не даёт модалке менять размер в зависимости от месяца.
const CALENDAR_WEEK_ROW_COUNT = 6;

// ============ CalendarNavButton ============
// Figma «Calendar ranger» → buttons (40×40, radius 12, neutral-50/5, blur 21px, иконка 20px)

interface CalendarNavButtonProps extends AriaButtonProps {
  children: React.ReactNode;
}

const CalendarNavButton = ({ children, ...props }: CalendarNavButtonProps) => {
  const ref = React.useRef<HTMLButtonElement>(null);
  const { buttonProps } = useButton(props, ref);

  return (
    <button
      {...buttonProps}
      ref={ref}
      className={cn(
        // Layout
        "flex size-10 shrink-0 items-center justify-center",
        // Background & Border
        "glass-blur rounded bg-neutral-50/5",
        // States
        "cursor-pointer transition-colors outline-none",
        "hover:bg-neutral-50/12",
        "disabled:cursor-not-allowed disabled:opacity-50"
      )}
    >
      {children}
    </button>
  );
};

// ============ CalendarCell ============
// Figma «calendar-dates»: 24×24, круг, neutral-50/12 + neutral-200.
// Выбранный день — neutral-100 / neutral-900. Пунктирное кольцо (hover / selected)
// живёт в CalendarCell.css: класс `is-selected` там только показывает кольцо (::after),
// цвета задаются классами ниже.

interface CalendarCellProps {
  state: CalendarState;
  date: CalendarDate;
}

const CalendarCell = ({ state, date }: CalendarCellProps) => {
  const ref = React.useRef<HTMLButtonElement>(null);
  const { cellProps, buttonProps, isSelected, isDisabled, isFocused, formattedDate } =
    useCalendarCell({ date }, state, ref);

  const isCurrentDay = isToday(date, getLocalTimeZone());

  return (
    <td
      {...cellProps}
      className="p-0"
    >
      <button
        {...buttonProps}
        ref={ref}
        className={cn(
          // Layout
          "calendar-cell relative flex size-6 min-w-6 items-center justify-center p-0",
          // Typography
          "fs-100 font-bold",
          // Background & Border
          "rounded-full bg-neutral-50/12",
          // Typography (color)
          "text-neutral-200",
          // States
          "cursor-pointer transition-colors outline-none",
          // Сегодня — без отдельного состояния в макете, подсвечиваем текстом
          isCurrentDay && !isSelected && "text-neutral-50",
          // В фокусе: подложка гаснет до neutral-50/5, показывается пунктирное кольцо
          isFocused && !isSelected && "is-selected bg-neutral-50/5",
          // Выбранный день: светлый круг
          isSelected && "is-selected bg-neutral-100 text-neutral-900",
          // Disabled
          isDisabled && "cursor-not-allowed opacity-50"
        )}
      >
        <span className="-translate-y-[0.5px]">{formattedDate}</span>
      </button>
    </td>
  );
};

// ============ CalendarGrid ============
// Figma «Date Column»: 192px (7 × 24 + 6 × 4), колонка недель с gap 4px

interface CalendarGridProps {
  state: CalendarState;
}

const CalendarGrid = ({ state }: CalendarGridProps) => {
  const { locale } = useLocale();
  const { gridProps, headerProps, weekDays } = useCalendarGrid({}, state);
  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

  return (
    <table
      {...gridProps}
      className="w-48 shrink-0 border-collapse"
    >
      {/* Дни недели скрыты по макету */}
      <thead
        {...headerProps}
        className="sr-only"
      >
        {/* Ключ — индекс, а не подпись: короткие названия дней недели повторяются
            (ru: «С» суббота и «С» среда), и React ругался на дубликаты ключей. */}
        <tr>
          {weekDays.map((day, index) => (
            <th key={index}>{day}</th>
          ))}
        </tr>
      </thead>
      <tbody className="flex flex-col gap-1">
        {Array.from({ length: CALENDAR_WEEK_ROW_COUNT }, (_, weekIndex) => (
          <tr
            key={weekIndex}
            className="flex items-center gap-1"
          >
            {state.getDatesInWeek(weekIndex).map((date, dayIndex) =>
              date && weekIndex < weeksInMonth ? (
                <CalendarCell
                  key={date.toString()}
                  state={state}
                  date={date}
                />
              ) : (
                <td
                  key={`empty-${weekIndex}-${dayIndex}`}
                  className="size-6 p-0"
                />
              )
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ============ CalendarGhostColumn ============
// Figma: слева и справа от активного месяца видны колонки соседних месяцев с opacity 5% —
// края сетки «затухают». Чисто декоративный слой: без фокуса, без обработчиков.

interface CalendarGhostColumnProps {
  month: DateValue;
}

const CalendarGhostColumn = ({ month }: CalendarGhostColumnProps) => {
  const { locale } = useLocale();

  const gridStart = startOfWeek(month, locale);

  return (
    <div
      aria-hidden
      className="pointer-events-none flex w-48 shrink-0 flex-col gap-1 opacity-5 select-none"
    >
      {Array.from({ length: CALENDAR_WEEK_ROW_COUNT }, (_, weekIndex) => (
        <div
          key={weekIndex}
          className="flex items-center gap-1"
        >
          {Array.from({ length: 7 }, (_, dayIndex) => {
            const date = gridStart.add({ days: weekIndex * 7 + dayIndex });

            if (date.month !== month.month) {
              return (
                <span
                  key={dayIndex}
                  className="size-6 shrink-0"
                />
              );
            }

            return (
              <span
                key={dayIndex}
                className={cn(
                  // Layout
                  "flex size-6 shrink-0 items-center justify-center",
                  // Background & Border
                  "rounded-full bg-neutral-50/12",
                  // Typography
                  "fs-100 font-bold text-neutral-200"
                )}
              >
                {date.day}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ============ Calendar ============

export interface CalendarProps<T extends DateValue = DateValue> extends Omit<
  AriaCalendarProps<T>,
  "createCalendar"
> {
  className?: string;
}

/**
 * Интерфейс приложения русский, но <I18nProvider> нигде не смонтирован, поэтому
 * useLocale() из react-aria брал локаль браузера: месяц назывался «July», а неделя
 * начиналась с воскресенья. Локаль задаём здесь, у самого календаря.
 */
export function Calendar<T extends DateValue = DateValue>(props: CalendarProps<T>) {
  return (
    <I18nProvider locale="ru-RU">
      <CalendarContent {...props} />
    </I18nProvider>
  );
}

function CalendarContent<T extends DateValue = DateValue>({
  className,
  ...props
}: CalendarProps<T>) {
  const { locale } = useLocale();

  const state = useCalendarState({
    ...props,
    locale,
    createCalendar,
  });

  const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(props, state);

  // В макете в шапке только название месяца, без года
  const monthFormatter = useDateFormatter({ month: "long" });
  const focusedDate = state.focusedDate;
  const monthName = monthFormatter.format(focusedDate.toDate(getLocalTimeZone()));

  const visibleMonth = state.visibleRange.start;

  return (
    <div
      {...calendarProps}
      className={cn("flex w-full min-w-0 flex-col items-center gap-3", className)}
    >
      {/* Month Container */}
      <div className="flex w-full items-center justify-between">
        <CalendarNavButton {...prevButtonProps}>
          <Icon
            name="angle-left"
            className="size-5 text-neutral-50"
          />
        </CalendarNavButton>

        <div className="flex min-w-0 flex-1 items-center justify-center select-none">
          <p className="fs-500 flex-1 text-center font-bold text-white capitalize">{monthName}</p>
        </div>

        <CalendarNavButton {...nextButtonProps}>
          <Icon
            name="angle-right"
            className="size-5 text-neutral-50"
          />
        </CalendarNavButton>
      </div>

      {/* Центральная сетка остаётся активной; соседние месяцы — только декоративные края. */}
      <div className="grid w-full grid-cols-[minmax(0,1fr)_min-content_minmax(0,1fr)] items-start gap-4 overflow-hidden">
        <div className="flex min-w-0 justify-end overflow-hidden">
          <CalendarGhostColumn month={visibleMonth.subtract({ months: 1 })} />
        </div>

        <CalendarGrid state={state} />

        <div className="flex min-w-0 overflow-hidden">
          <CalendarGhostColumn month={visibleMonth.add({ months: 1 })} />
        </div>
      </div>
    </div>
  );
}
